import { type FC, useCallback, useState } from 'react'

import { useUnit } from 'effector-react'

import PlusIcon from '@/assets/icons/icon-plus.svg'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import type { DialogProps } from '@/dialogs/types'
import { addGlossaryEntryFx } from '@/modules/ai-features/models/glossary-settings.ts'
import MinusIcon from '@/modules/chatting/icons/minus.svg'
import { IconButton } from '@/modules/chatting/ui/components/icon-button.tsx'

export interface Props extends DialogProps {}

export const SmartGlossaryAIDialog: FC<Props> = ({ isOpen, onClose }) => {
  const [word, setWord] = useState('')
  const [translation, setTranslation] = useState('')
  const [keepOriginal, setKeepOriginal] = useState(true)

  const isLoading = useUnit(addGlossaryEntryFx.pending)

  const handleWordChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setWord(event.target.value)
    },
    [],
  )

  const handleTranslationChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setTranslation(event.target.value)
    },
    [],
  )

  const handleKeepOriginalChange = useCallback(() => {
    setKeepOriginal(!keepOriginal)
  }, [keepOriginal])

  const handleAddWord = () => {
    const wordTrimmed = word.trim()

    if (!wordTrimmed) return

    addGlossaryEntryFx({
      original: wordTrimmed,
      translation: keepOriginal ? wordTrimmed : translation.trim(),
    })

    setWord('')
    setTranslation('')
    setKeepOriginal(true)
  }

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
    >
      <Dialog.Header>
        <Dialog.Title>Add Word</Dialog.Title>
        <Dialog.Description className="text-center">
          Add words that should not be translated, or add individual
          translations for words.
        </Dialog.Description>
      </Dialog.Header>

      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <label
            htmlFor="word-input"
            className="text-sm font-medium text-text-default"
          >
            Word
          </label>
          <TextField
            id="word-input"
            placeholder='e.g. "daddy"'
            value={word}
            onChange={handleWordChange}
            disabled={isLoading}
          />
        </div>

        {!keepOriginal && (
          <div className="flex flex-col gap-2">
            <label
              htmlFor="translation-input"
              className="text-sm font-medium text-default"
            >
              Translation
            </label>
            <TextField
              id="translation-input"
              placeholder='e.g. "Reyna"'
              value={translation}
              onChange={handleTranslationChange}
              disabled={keepOriginal || isLoading}
            />
          </div>
        )}

        <div
          className="flex items-center gap-2"
          onClick={handleKeepOriginalChange}
        >
          <IconButton
            icon={keepOriginal ? PlusIcon : MinusIcon}
            className="w-9 h-9 rounded-md border-[1px] border-secondary-400"
          />

          <span className="text-sm  font-semibold">
            {keepOriginal ? 'Add individual' : 'Remove'} translation
          </span>
        </div>
      </div>

      <Dialog.Footer className="w-full">
        <Button
          id="tete"
          variant="outline"
          className="w-1/2"
          onClick={onClose}
          disabled={isLoading}
        >
          Close
        </Button>
        <Button
          className="w-1/2"
          onClick={handleAddWord}
          disabled={isLoading || !word || (!keepOriginal && !translation)}
        >
          <PlusIcon /> Add Word
        </Button>
      </Dialog.Footer>
    </Dialog>
  )
}
